import * as pdfjsLib from 'pdfjs-dist/webpack';

// Map to track PDF viewer instances by canvas ID
const pdfViewerInstances = new Map();

// Create a basic viewer instance
function createViewerInstance() {
    return {
        pdfDoc: null,
        pageNum: 1,
        currentRenderTask: null,
        isRendering: false  // Track rendering state
    };
}

// Main PDF viewer object - maintains backward compatibility
const pdfViewer = {
    // Default instance properties for backward compatibility
    pdfDoc: null,
    pageNum: 1,
    currentRenderTask: null,

    // Get a viewer instance for a specific canvas
    getInstance: function (canvasId) {
        // Default to "pdf-canvas" for backward compatibility
        canvasId = canvasId || "pdf-canvas";

        // Create new instance if it doesn't exist
        if (!pdfViewerInstances.has(canvasId)) {
            pdfViewerInstances.set(canvasId, createViewerInstance());
        }

        return pdfViewerInstances.get(canvasId);
    },

    // Render a page for a specific canvas
    renderPage: function (num, canvasId, rotation = 0) {
        return new Promise((resolve, reject) => {
            // Default to "pdf-canvas" for backward compatibility
            canvasId = canvasId || "pdf-canvas";

            const canvas = document.getElementById(canvasId);
            if (!canvas) {
                const error = `Canvas with ID "${canvasId}" not found`;
                console.error(error);
                reject(new Error(error));
                return;
            }

            const instance = this.getInstance(canvasId);
            if (!instance.pdfDoc) {
                const error = `No PDF document loaded for canvas "${canvasId}"`;
                console.error(error);
                reject(new Error(error));
                return;
            }

            // If already rendering, cancel the current task and wait a bit
            if (instance.isRendering) {
                console.log(`Cancelling current render for canvas ${canvasId}`);
                if (instance.currentRenderTask) {
                    instance.currentRenderTask.cancel();
                    instance.currentRenderTask = null;
                }

                // Wait a bit to ensure cancellation completes
                setTimeout(() => {
                    instance.isRendering = false;
                    this.renderPage(num, canvasId, rotation).then(resolve).catch(reject);
                }, 50);
                return;
            }

            const ctx = canvas.getContext("2d");

            instance.isRendering = true; // Mark as rendering
            ctx.clearRect(0, 0, canvas.width, canvas.height);  // Clear the canvas before rendering a new page

            // Get the page
            instance.pdfDoc.getPage(num).then((page) => {
                rotation = typeof rotation === 'number' ? rotation : (instance.rotation || 0);
                const viewport = page.getViewport({ scale: 1.5, rotation: rotation });
                canvas.height = viewport.height;
                canvas.width = viewport.width;

                const renderContext = {
                    canvasContext: ctx,
                    viewport: viewport
                };

                // Store the current render task
                instance.currentRenderTask = page.render(renderContext);

                return instance.currentRenderTask.promise.then(() => {
                    instance.currentRenderTask = null;
                    instance.isRendering = false;
                    console.log(`Page ${num} rendered successfully on canvas ${canvasId}`);
                    resolve();
                }).catch((error) => {
                    if (error && error.name === 'RenderingCancelled') {
                        console.log(`Rendering cancelled for canvas ${canvasId}`);
                    } else {
                        console.error(`Error rendering page for canvas ${canvasId}:`, error);
                    }
                    instance.currentRenderTask = null;
                    instance.isRendering = false;
                    reject(error);
                });
            }).catch(error => {
                console.error(`Error getting page ${num} for canvas ${canvasId}:`, error);
                instance.isRendering = false;
                reject(error);
            });
        });
    },

    // Create a blob URL from a base64 string
    createBlobUrl: function (base64String) {
        try {
            // Check if the string starts with data URI prefix and remove if present
            if (base64String.startsWith('data:application/pdf;base64,')) {
                base64String = base64String.substring('data:application/pdf;base64,'.length);
            }

            // Convert Base64 to binary
            let byteCharacters = atob(base64String);
            let byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            let byteArray = new Uint8Array(byteNumbers);

            // Create a Blob from the byte array
            let blob = new Blob([byteArray], { type: 'application/pdf' });

            // Generate a Blob URL
            return URL.createObjectURL(blob);
        } catch (error) {
            console.error("Error creating blob URL from base64:", error);
            return null;
        }
    },

    // Load a PDF for a specific canvas
    loadPDF: async function (url, canvasId) {
        // Default to "pdf-canvas" for backward compatibility
        canvasId = canvasId || "pdf-canvas";

        const instance = this.getInstance(canvasId);

        // Clean up existing document if there is one
        if (instance.pdfDoc) {
            await instance.pdfDoc.destroy();
            instance.pdfDoc = null;
        }

        // Cancel any ongoing render
        if (instance.currentRenderTask) {
           await instance.currentRenderTask.cancel();
            instance.currentRenderTask = null;
        }

        // Reset rendering flag
        instance.isRendering = false;

        return pdfjsLib.getDocument(url).promise.then(pdfDoc_ => {
            instance.pdfDoc = pdfDoc_;
            instance.pageNum = 1;

            // For backward compatibility, also set on the main object
            if (canvasId === "pdf-canvas") {
                this.pdfDoc = pdfDoc_;
                this.pageNum = 1;
            }

            // Render first page after a small delay to ensure everything is initialized
            setTimeout(() => {
                this.renderPage(instance.pageNum, canvasId);
            }, 50);

            console.log(`PDF loaded for canvas ${canvasId} with ${pdfDoc_.numPages} pages`);
            return pdfDoc_.numPages;
        }).catch(err => {
            console.error(`PDF loading error for canvas ${canvasId}:`, err);
            return 0;
        });
    },

    // Go to a specific page
    goToPage: function (num, canvasId) {
        // Default to "pdf-canvas" for backward compatibility
        canvasId = canvasId || "pdf-canvas";

        const instance = this.getInstance(canvasId);

        if (!instance.pdfDoc) {
            console.warn(`Cannot go to page, no PDF loaded for canvas ${canvasId}`);
            return Promise.resolve(false);
        }

        // Validate page number
        if (num < 1) {
            num = 1;
        } else if (num > instance.pdfDoc.numPages) {
            num = instance.pdfDoc.numPages;
        }

        instance.pageNum = num;

        // For backward compatibility, also set on the main object
        if (canvasId === "pdf-canvas") {
            this.pageNum = num;
        }

        return this.renderPage(num, canvasId)
            .then(() => true)
            .catch(err => {
                console.error(`Error navigating to page ${num}:`, err);
                return false;
            });
    },

    // Rotate the current page and re-render it
    rotatePage : function (rotation, canvasId) {
        canvasId = canvasId || "pdf-canvas";
        const instance = this.getInstance(canvasId);

        if (!instance || !instance.pdfDoc) {
            console.warn(`Cannot rotate page, no PDF loaded for canvas ${canvasId}`);
            return Promise.resolve(false);
        }

        if (typeof rotation !== 'number') {
            console.warn(`Invalid rotation value: ${rotation}`);
            return Promise.resolve(false);
        }

        // Store rotation on the instance (0, 90, 180, 270)
        instance.rotation = rotation;

        return this.renderPage(instance.pageNum, canvasId, rotation)
            .then(() => true)
            .catch(err => {
                console.error(`Error rotating page on canvas ${canvasId}:`, err);
                return false;
            });
    },


    // Clean up resources for a specific canvas
    cleanup: function (canvasId) {
        // Default to "pdf-canvas" for backward compatibility
        canvasId = canvasId || "pdf-canvas";

        if (pdfViewerInstances.has(canvasId)) {
            const instance = pdfViewerInstances.get(canvasId);

            // Cancel any ongoing render task
            if (instance.currentRenderTask) {
                instance.currentRenderTask.cancel();
                instance.currentRenderTask = null;
            }

            // Reset rendering flag
            instance.isRendering = false;

            // Destroy PDF document
            if (instance.pdfDoc) {
                instance.pdfDoc.destroy();
                instance.pdfDoc = null;
            }

            // Remove from instances map
            pdfViewerInstances.delete(canvasId);
            console.log(`Cleaned up PDF viewer for canvas ${canvasId}`);
        }
    }
};

// Additional methods specifically for Blazor components

// Load PDF with a specific canvas ID
window.loadPdfWithCanvas = function (url, canvasId) {
    return pdfViewer.loadPDF(url, canvasId);
};

// Go to page with a specific canvas ID
window.goToPageWithCanvas = function (num, canvasId) {
    return pdfViewer.goToPage(num, canvasId);
};

// Rotate the current page and re-render it
window.rotatePdfPageWithCanvas = function (rotation, canvasId) {
    return pdfViewer.rotatePage(rotation, canvasId);
};

// Cleanup a specific instance
window.cleanupPdfViewerInstance = function (canvasId) {
    pdfViewer.cleanup(canvasId);
    return true;
};

// Expose the PDF viewer object globally for Blazor JS interop
window.pdfViewer = pdfViewer;

//DOCX Viewer
import { renderAsync } from "docx-preview";

window.docxViewer = {
    preview: async (bytes, elementId) => {
        console.log(bytes)
        const container = document.getElementById(elementId);

        if (!container) {
            console.error("DOCX container not found:", elementId);
            return;
        }

        container.innerHTML = "";

        const arrayBuffer = new Uint8Array(bytes).buffer;

        await renderAsync(
            arrayBuffer,
            container,
            container,
            {
                inWrapper: true,
                ignoreWidth: false,
                ignoreHeight: false
            }
        );
    }
};